import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import {
    FieldDefinition,
    FormDefinition,
    FormElementLayoutDefinition,
    FormInitialisationMode,
    FormLayout,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import { Subject } from 'rxjs';
import {
    IFormDataResult,
    IFormPendingChange,
} from '../../constants/control-types';

@Injectable()
export class FormStaticService extends WdxDestroyClass {
    formDefinition!: FormDefinition;
    childFormDefinitions!: Record<string, FormDefinition>;
    formLayout!: FormLayout;
    initialisationMode!: FormInitialisationMode;
    form!: UntypedFormGroup;
    formId!: string;
    formData!: IFormDataResult;
    submitAttempted$ = new Subject<boolean>();
    fieldsToNullCheck: string[] = [];
    pendingChangesSubForm: {
        [key: string]: {
            [key: string]: {
                [key: string]: IFormPendingChange;
            };
        };
    } = {};
    isReadonlyMode = false;

    /**
     * Gets layout for a field path e.g. 'field1', 'field1.subfield1', 'field1[0].subfield1'
     *
     * Important - this is the layout from the form defintion provided by the API, does not include changes
     * from conditions or functions
     *
     * @param fieldPath
     * @param definition
     * @returns
     */
    public getLayoutForFieldFromFormDef(
        fieldPath: string,
    ): FormElementLayoutDefinition | null | undefined {
        return this.getLayoutForFieldFromFormDefinition(
            this.cleanseFieldPath(fieldPath),
        );
    }

    private getLayoutForFieldFromFormDefinition(
        fieldPath: string,
        layouts?: FormElementLayoutDefinition[] | null | undefined,
    ): FormElementLayoutDefinition | null | undefined {
        const fieldIsSubform = fieldPath.includes('.');
        if (!layouts) {
            layouts =
                this.formDefinition?.layout?.sectionLayoutDefinitions?.flatMap(
                    ({ elementLayoutDefinitions }) => elementLayoutDefinitions,
                ) as FormElementLayoutDefinition[];
        }
        if (fieldIsSubform) {
            const pathParts = fieldPath.split('.');
            return this.getLayoutForFieldFromFormDefinition(
                pathParts.slice(1).join('.'),
                layouts?.find((eld) => eld?.name === pathParts[0])
                    ?.sectionLayoutDefinitions?.[0].elementLayoutDefinitions,
            );
        }
        return layouts?.find((eld) => eld?.name === fieldPath);
    }

    /**
     * Gets definition for a field path e.g. 'field1', 'field1.subfield1', 'field1[0].subfield1'
     *
     * Important - this is from the form defintion provided by the API, does not include changes
     * from conditions or functions that update schema
     *
     * @param fieldPath
     * @returns
     */
    public getFieldDefinitionForFieldFromFormDef(
        fieldPath: string,
    ): FieldDefinition | null | undefined {
        return this.getFieldDefinitionFromParentFieldDefintion(
            this.cleanseFieldPath(fieldPath),
        );
    }

    /**
     * Removes all bracket variants from a field path so give a dot separated path only e.g. all these
     * 'members[].comment'
     * 'members[0].comment'
     * 'members.[]comment'
     * 'members.[0]comment'
     * 'members.[].comment'
     * 'members.[0].comment'
     *
     * would return 'members.comment'
     *
     * @param fieldPath
     * @returns cleaned string
     */
    public cleanseFieldPath(fieldPath: string) {
        return fieldPath.replaceAll(/\.?\[\d*\]\.?/g, '.');
    }

    public isValueFromDataContext(fieldPath: string): boolean {
        // TODO: generate below list from form-context.ts
        // - need to update service generation script to also create a list of class property names to achieve this
        const contextRoots = [
            '$appStatus',
            '$activityExternalTask',
            '$appType',
            '$bookingCentre',
            '$case',
            '$client',
            '$clientProduct',
            '$dateFinished',
            '$dateLastUpdated',
            '$dateNextReviewDue',
            '$dateStarted',
            '$externalFormSummary',
            '$lastUpdatedBy',
            '$originatingEntityType',
            '$party',
            '$partyRole',
            '$process',
            '$productGroup',
            '$servicingCentre',
            '$userPrivileges',
            '$userRoles',
        ];

        return contextRoots.includes(fieldPath.split('.')[0]);
    }

    public getValueFromFormDataContext(fieldPath: string) {
        if (!this.formData.context || !this.isValueFromDataContext(fieldPath)) {
            return;
        }

        const pathParts = this.cleanseFieldPath(fieldPath.slice(1)).split('.');
        let contextDrilldown = this.formData.context;
        pathParts.forEach((part) => {
            contextDrilldown = this.getValueFromContextData(
                part,
                contextDrilldown ?? {},
            );
        });
        return contextDrilldown;
    }

    private getValueFromContextData(
        name: string,
        context: Record<string, any>,
    ) {
        return context[name];
    }

    private getFieldDefinitionFromParentFieldDefintion(
        fieldPath: string,
        definition?: FieldDefinition,
    ): FieldDefinition | null | undefined {
        let schema: FieldDefinition[] | null | undefined;
        if (!definition) {
            schema = this.formDefinition.schema;
        } else {
            schema = definition.childSchema;
        }

        const fieldIsSubform = fieldPath.includes('.');
        const name = fieldIsSubform ? fieldPath.split('.')[0] : fieldPath;
        return fieldIsSubform
            ? this.getFieldDefinitionFromParentFieldDefintion(
                  fieldPath.split('.').slice(1).join('.'),
                  schema?.find((eld) => eld?.name === name),
              )
            : schema?.find((eld) => eld?.name === name);
    }
}
